import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { referralsAPI } from '../lib/api';
import { 
  Users, Gift, Copy, Trophy, Medal, Award, 
  Loader2, CheckCircle, Clock, AlertCircle,
  Calendar, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

export const ReferralCenter = () => {
  const { user, hasPerm } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tenantStats, setTenantStats] = useState(null);
  const [myStats, setMyStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [myRes, tenantRes] = await Promise.all([
        referralsAPI.getMyReferrals(),
        hasPerm('referrals.view_team') ? referralsAPI.getTenantStats() : Promise.resolve(null),
      ]);
      setMyStats(myRes.data);
      if (tenantRes) setTenantStats(tenantRes.data);
    } catch (error) {
      console.error('Failed to load referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = (code) => {
    if (code) {
      navigator.clipboard.writeText(code);
      toast.success('Referral code copied!');
    }
  };

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (index === 1) return <Medal className="w-5 h-5 text-gray-400" />;
    if (index === 2) return <Award className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 text-center text-muted-foreground font-medium">{index + 1}</span>;
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: { color: 'bg-purple-100 text-purple-700', label: 'Owner' },
      office: { color: 'bg-blue-100 text-blue-700', label: 'Office' },
      tech: { color: 'bg-green-100 text-green-700', label: 'Tech' },
    };
    const config = styles[role] || styles.tech;
    return <Badge className={`${config.color} text-xs`}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold font-heading">Referral Center</h1>
            <p className="text-sm text-muted-foreground">
              Track referrals and earn free months
            </p>
          </div>
        </div>

        {/* Tenant Summary Stats (Owner view) */}
        {tenantStats && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Team Overview
              </CardTitle>
              <CardDescription>
                {tenantStats.tenant_name} - Company-wide referral performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-3xl font-bold">{tenantStats.total_referrals}</p>
                  <p className="text-sm text-muted-foreground">Total Referrals</p>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-3xl font-bold">{tenantStats.qualified_referrals}</p>
                  <p className="text-sm text-muted-foreground">Qualified</p>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-3xl font-bold">{tenantStats.pending_referrals}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
                <div className="text-center p-4 bg-green-100 rounded-lg">
                  <p className="text-3xl font-bold text-green-700">
                    {tenantStats.free_months_available}
                  </p>
                  <p className="text-sm text-green-600">Free Months Available</p>
                </div>
              </div>
              
              {tenantStats.last_reward_date && (
                <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  Last reward earned: {new Date(tenantStats.last_reward_date).toLocaleDateString()}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Team Leaderboard (Owner view) */}
        {tenantStats?.leaderboard && tenantStats.leaderboard.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Team Leaderboard
              </CardTitle>
              <CardDescription>
                Referral performance by team member
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {tenantStats.leaderboard.map((member, index) => (
                  <div 
                    key={member.user_id}
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg transition-colors ${
                      member.user_id === user?.id ? 'bg-primary/5 border border-primary/20' : 'bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex items-center justify-center w-8 shrink-0">
                        {getRankIcon(index)}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium truncate">{member.name}</p>
                          {member.user_id === user?.id && (
                            <Badge variant="outline" className="text-xs">You</Badge>
                          )}
                          {getRoleBadge(member.role)}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono">
                            {member.referral_code}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-5 w-5"
                            onClick={() => copyReferralCode(member.referral_code)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm pl-11 sm:pl-0 shrink-0">
                      <div className="text-center">
                        <p className="font-bold">{member.total_referrals}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-green-600">{member.qualified_referrals}</p>
                        <p className="text-xs text-muted-foreground">Qualified</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-yellow-600">{member.pending_referrals}</p>
                        <p className="text-xs text-muted-foreground">Pending</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Referral Code */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gift className="w-5 h-5 text-primary" />
              Your Referral Code
            </CardTitle>
            <CardDescription>
              Share your personal code. When they pay, you earn a free month!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Input
                value={myStats?.referral_code || ''}
                readOnly
                className="font-mono text-lg max-w-[200px] font-bold"
              />
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => copyReferralCode(myStats?.referral_code)}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            {/* Personal Stats */}
            <div className="grid grid-cols-3 gap-4 pt-2">
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{myStats?.total_referrals || 0}</p>
                <p className="text-xs text-muted-foreground">Referrals</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{myStats?.qualified_referrals || 0}</p>
                <p className="text-xs text-muted-foreground">Qualified</p>
              </div>
              <div className="text-center p-3 bg-green-100 rounded-lg">
                <p className="text-2xl font-bold text-green-700">
                  {myStats?.free_months_earned || 0}
                </p>
                <p className="text-xs text-green-600">Free Months Earned</p>
              </div>
            </div>

            {/* My Referrals List */}
            {myStats?.referrals?.length > 0 && (
              <div className="pt-4 border-t">
                <h4 className="font-semibold mb-3">Your Referrals</h4>
                <div className="space-y-2">
                  {myStats.referrals.map((ref) => (
                    <div 
                      key={ref.id}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">
                          {ref.referred_tenant_name || ref.referred_email || 'Pending signup'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(ref.created_at).toLocaleDateString()}
                          {ref.qualified_at && (
                            <span className="ml-2 text-green-600">
                              • Qualified {new Date(ref.qualified_at).toLocaleDateString()}
                            </span>
                          )}
                        </p>
                      </div>
                      <Badge 
                        className={
                          ref.status === 'qualified' 
                            ? 'bg-green-100 text-green-700' 
                            : ref.status === 'disqualified'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }
                      >
                        {ref.status_label || ref.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {(!myStats?.referrals || myStats.referrals.length === 0) && (
              <div className="text-center py-6 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No referrals yet</p>
                <p className="text-sm">Share your code to start earning free months!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* How it works */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">How Referrals Work</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">
                  1
                </div>
                <div>
                  <p className="font-medium">Share Your Code</p>
                  <p className="text-sm text-muted-foreground">
                    Give your unique referral code to a friend
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">
                  2
                </div>
                <div>
                  <p className="font-medium">They Sign Up</p>
                  <p className="text-sm text-muted-foreground">
                    Your friend creates an account using your code
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">
                  3
                </div>
                <div>
                  <p className="font-medium">Earn Free Month</p>
                  <p className="text-sm text-muted-foreground">
                    When they pay their first invoice, you get a free month!
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
